import React, { useState, useEffect } from "react";

// ============================================================
// 기본지역 설정 카드 (관리 탭)
//
// 하는 일:
//   - GET  /api/staff-intake/region 으로 현재 설정을 불러온다
//   - 시도 / 시군구(여러 개) / 각 구의 법정동 목록을 편집한다
//   - POST /api/staff-intake/region 으로 저장한다 (admin 만 가능)
//
// 왜 필요한가:
//   직원 물건접수폼(/staff/intake)이 이 설정을 읽어
//   구 버튼과 동 버튼을 그린다. 여기서 바꾸면 코드 수정 없이
//   다루는 지역을 늘리거나 줄일 수 있다.
//
// 왜 동을 사람이 지정하나:
//   매물 DB 에서 자동으로 뽑으면 과거 오입력(신길동·상도동 각 1건)이 섞이고,
//   '은천동' 같은 행정동을 고르면 "관악구 은천동 123" 이라는 없는 주소가 되어
//   건물정보 조회(PNU 변환)가 실패한다. 주소로 쓸 수 있는 건 법정동뿐이다.
//
// 연관 파일:
//   - backend/routes/staffIntake.js : region API + emd(자동채우기) API
//   - pages/StaffIntake.jsx         : 이 설정을 읽어 쓰는 화면
//   - App.js 관리탭                  : 이 카드를 배치하는 곳
//
// ※ App.js 안이 아니라 별도 파일로 둔 이유는 SliderConfigManager 와 같다.
//   컴포넌트를 다른 컴포넌트 안에 정의하면 부모가 다시 그려질 때마다
//   새 컴포넌트로 인식되어 입력 중 포커스가 날아간다.
// ============================================================

// 시도 목록 — 코드는 행정표준코드(시도 2자리)
const SIDO_LIST = [
  { name: "서울특별시", code: "11" },
  { name: "부산광역시", code: "26" },
  { name: "대구광역시", code: "27" },
  { name: "인천광역시", code: "28" },
  { name: "광주광역시", code: "29" },
  { name: "대전광역시", code: "30" },
  { name: "울산광역시", code: "31" },
  { name: "세종특별자치시", code: "36" },
  { name: "경기도", code: "41" },
  { name: "강원특별자치도", code: "51" },
  { name: "충청북도", code: "43" },
  { name: "충청남도", code: "44" },
  { name: "전북특별자치도", code: "52" },
  { name: "전라남도", code: "46" },
  { name: "경상북도", code: "47" },
  { name: "경상남도", code: "48" },
  { name: "제주특별자치도", code: "50" },
];

export default function RegionConfigManager({ cardStyle, titleStyle, subTitleStyle }) {
  const [cfg, setCfg]         = useState(null);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg]         = useState("");
  const [sigunguList, setSigunguList] = useState([]);  // 선택한 시도의 구 목록

  // 토큰을 붙인 fetch — 이 API 들은 로그인 상태여야 응답한다
  const authGet = (url) =>
    fetch(url, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    });

  // ── 최초 1회 불러오기 ──────────────────────────────────
  useEffect(() => {
    let alive = true;
    authGet("/api/staff-intake/region")
      .then((r) => r.json())
      .then((d) => {
        if (!alive) return;
        setCfg(d.region || null);
        setLoading(false);
        if (d.region?.sidoCd) loadSigungu(d.region.sidoCd);
      })
      .catch(() => {
        if (alive) { setMsg("설정을 불러오지 못했습니다."); setLoading(false); }
      });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── 시군구 목록 (구 추가 시 선택용) ────────────────────
  //   기존 건축물검색 API 재사용. 관리탭은 admin 만 들어오므로 권한 문제 없음.
  const loadSigungu = async (sidoCd) => {
    try {
      const r = await authGet(`/api/building-search/regions/sigungu?sidoCd=${sidoCd}`);
      const j = await r.json();
      setSigunguList(Array.isArray(j.list) ? j.list : []);
    } catch (_) {
      setSigunguList([]);
    }
  };

  // ── 편집 헬퍼 ─────────────────────────────────────────
  const changeSido = (code) => {
    const found = SIDO_LIST.find((s) => s.code === code);
    setCfg((c) => ({ ...c, sido: found?.name || "", sidoCd: code, regions: [] }));
    loadSigungu(code);
    setMsg("시도를 바꾸면 구 목록을 다시 지정해야 합니다.");
  };

  const addGungu = (sigunguCd) => {
    if (!sigunguCd) return;
    const found = sigunguList.find((s) => String(s.code) === String(sigunguCd));
    if (!found) return;
    setCfg((c) => {
      // 이미 있으면 중복 추가하지 않는다
      if ((c.regions || []).some((r) => r.sigunguCd === String(found.code))) return c;
      return {
        ...c,
        regions: [...(c.regions || []),
                  { gungu: found.name, sigunguCd: String(found.code), dongs: [] }],
      };
    });
  };

  const removeGungu = (idx) =>
    setCfg((c) => ({ ...c, regions: c.regions.filter((_, i) => i !== idx) }));

  // 기본 지역 지정 — 고른 구를 목록 맨 앞으로 옮긴다.
  //   접수폼과 지도 시작 위치가 모두 첫 번째 구를 따르므로,
  //   순서를 바꾸는 것만으로 기본 지역이 정해진다.
  const setPrimary = (idx) =>
    setCfg((c) => {
      const list = [...c.regions];
      const [picked] = list.splice(idx, 1);
      return { ...c, regions: [picked, ...list] };
    });

  // 동 목록은 쉼표로 구분된 한 줄 문자열로 편집한다 (입력이 가장 빠름)
  const setDongs = (idx, text) =>
    setCfg((c) => ({
      ...c,
      regions: c.regions.map((r, i) =>
        i === idx
          ? { ...r, dongs: text.split(",").map((d) => d.trim()).filter(Boolean) }
          : r
      ),
    }));

  // DB 에 등록된 매물에서 동을 뽑아 채운다 (오입력 걸러내려고 5건 이상만)
  const autoFillDongs = async (idx) => {
    const region = cfg.regions[idx];
    try {
      const r = await authGet(
        `/api/staff-intake/emd?gungu=${encodeURIComponent(region.gungu)}`
      );
      const j = await r.json();
      const list = (j.list || []).filter((d) => d.cnt >= 5).map((d) => d.name);
      if (list.length === 0) return setMsg("등록된 매물에서 동을 찾지 못했습니다.");
      setCfg((c) => ({
        ...c,
        regions: c.regions.map((r2, i) => (i === idx ? { ...r2, dongs: list } : r2)),
      }));
      setMsg(`${region.gungu}: ${list.length}개 동을 채웠습니다. 확인 후 저장하세요.`);
    } catch (e) {
      setMsg("자동 채우기 실패: " + e.message);
    }
  };

  // ── 저장 ──────────────────────────────────────────────
  const save = async () => {
    setMsg("저장 중...");
    try {
      const r = await fetch("/api/staff-intake/region", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({ region: cfg }),
      });
      const j = await r.json();
      if (!j.ok) throw new Error(j.error || "저장 실패");
      setCfg(j.region);   // 서버가 검증한 결과로 화면을 맞춘다
      setMsg("저장되었습니다.");
    } catch (e) {
      setMsg("저장 실패: " + e.message);
    }
  };

  // ── 화면 ──────────────────────────────────────────────
  if (loading) return <div style={cardStyle}>기본지역 설정을 불러오는 중...</div>;
  if (!cfg)    return <div style={cardStyle}>{msg || "설정 없음"}</div>;

  const box = { border: "1px solid #ddd", borderRadius: 8, padding: 12,
                marginBottom: 10, background: "#fff" };
  const input = { padding: "8px 10px", border: "1px solid #ccc",
                  borderRadius: 6, fontSize: 14 };
  const btn = { padding: "8px 14px", border: "1px solid #ccc", borderRadius: 6,
                background: "#f5f5f5", cursor: "pointer", fontSize: 14 };

  return (
    <div style={cardStyle}>
      <div style={titleStyle}>🏘️ 물건접수 지역 설정</div>
      <div style={subTitleStyle}>
        물건접수폼과 지도 시작 위치에 쓰이는 지역 목록입니다.
        <b>기본</b>으로 고른 구가 접수폼에서 미리 선택됩니다.
        동은 반드시 <b>법정동</b>이어야 합니다 (관악구 = 봉천동·신림동·남현동).
        행정동을 넣으면 건물정보 조회가 실패합니다.
      </div>

      {/* 시도 */}
      <div style={{ display: "flex", gap: 8, alignItems: "center", margin: "12px 0" }}>
        <span style={{ width: 60, fontSize: 14, color: "#666" }}>시도</span>
        <select style={input} value={cfg.sidoCd || ""} onChange={(e) => changeSido(e.target.value)}>
          {SIDO_LIST.map((s) => (
            <option key={s.code} value={s.code}>{s.name}</option>
          ))}
        </select>
      </div>

      {/* 구 목록 */}
      {(cfg.regions || []).map((r, idx) => (
        <div key={r.sigunguCd} style={box}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            {/* 기본 지역 선택 — 첫 번째 구가 기본이 된다 */}
            <label style={{ display: "flex", alignItems: "center", gap: 5,
                            cursor: "pointer", fontSize: 13 }}>
              <input
                type="radio"
                name="primaryRegion"
                checked={idx === 0}
                onChange={() => setPrimary(idx)}
                style={{ width: 16, height: 16 }}
              />
              {idx === 0 && (
                <span style={{ color: "#1f8a4c", fontWeight: 700 }}>기본</span>
              )}
            </label>
            <b style={{ fontSize: 15 }}>{r.gungu}</b>
            <span style={{ color: "#999", fontSize: 12 }}>코드 {r.sigunguCd}</span>
            <span style={{ flex: 1 }} />
            <button style={{ ...btn, color: "#c00" }} onClick={() => removeGungu(idx)}>
              삭제
            </button>
          </div>

          <div style={{ fontSize: 13, color: "#666", marginBottom: 4 }}>
            법정동 (쉼표로 구분)
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <input
              style={{ ...input, flex: 1 }}
              value={(r.dongs || []).join(", ")}
              onChange={(e) => setDongs(idx, e.target.value)}
              placeholder="봉천동, 신림동, 남현동"
            />
            <button style={btn} onClick={() => autoFillDongs(idx)}>
              DB에서 채우기
            </button>
          </div>
        </div>
      ))}

      {/* 구 추가 */}
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 10 }}>
        <span style={{ width: 60, fontSize: 14, color: "#666" }}>구 추가</span>
        <select style={input} value="" onChange={(e) => addGungu(e.target.value)}>
          <option value="">— 시군구 선택 —</option>
          {sigunguList.map((s) => (
            <option key={s.code} value={s.code}>{s.name}</option>
          ))}
        </select>
      </div>

      {/* 저장 */}
      <div style={{ marginTop: 16, display: "flex", gap: 10, alignItems: "center" }}>
        <button
          style={{ ...btn, background: "#2b6cb0", color: "#fff", borderColor: "#2b6cb0",
                   padding: "10px 24px", fontWeight: 700 }}
          onClick={save}
        >
          저장
        </button>
        {msg && <span style={{ fontSize: 13, color: "#666" }}>{msg}</span>}
      </div>
    </div>
  );
}